import { useCallback, useMemo, useReducer, useState } from 'react';
import { useLocation } from '@tanstack/react-router';
import {
  CompoundFilter,
  CompoundFilterProviderProps,
  CompoundFilterStateActions,
} from '@trukt/ui/CompoundFilter';
import { PageLayout } from '@trukt/ui/PageLayout';
import { Tag } from '@trukt/ui/Tag';
import { ConfigStorage } from '@trukt/portal-utils/configs-storage';
import {
  OrderListViewProps,
  HandleUpdateReportMutationProps,
  OrderReportColumnLiterals,
} from './OrderListView.types';
import {
  fromOrderListResponseToJobTableRows,
  getReportColumnVisibility,
  getTableColumnsFromReportColumns,
} from './OrderListView.helpers';
import { ORDER_COLUMNS, SORTABLE_FIELD_MAP } from './OrderListView.constants';

import { ReportName } from './components';
import { useOrdersQuery, useReportQuery, useUpdateReportMutation } from './hooks';

import { mapOrdersRequestValues, mapUpdateReportRequest, mapFilters } from './helpers';
import {
  buildInitialState,
  OrderListCompoundFilterState,
  useOrderListCompoundFilter,
} from './hooks/use-order-list-compound-filter';
import { SubTopicOptions } from './components/sub-topic-options';
import {
  fromOldUrlStateToNewFilterStructure,
  OrderListSearchParamsProvider,
  SEARCH_PARAM_KEYS,
  useOrderListSearchParamsContext,
  useOrderListRouteSearchParams,
} from './context';
import { ORDER_STATUS_MAP } from '@trukt/portal-utils/OrderStatusMap';
import { useDebounce } from '@trukt/react-hooks/useDebounce';
import { SortingState } from '@tanstack/react-table';
import { GetOrdersSort } from '@api';
import { ExternalCompoundFilterState } from '@trukt/ui/CompoundFilter';
import { OrderListContent } from './components/order-list-content';
import { useReactTableStateProps } from '@trukt/tables/useReactTableStateProps';
import { OrderListHeader } from './components/order-list-header';
import { OrderListTopic } from './components/order-list-topic';
import { OrderListPagination } from './components/order-list-pagination';
import { VisibilityState } from '@trukt/ui/columns-settings-card';

type State = {
  globalSearch: string;
  debouncedFilters: OrderListCompoundFilterState;
  didReportsFinishSetting: boolean;
};

type Action =
  | { type: 'SET_GLOBAL_SEARCH'; payload: State['globalSearch'] }
  | { type: 'SET_DEBOUNCED_FILTERS'; payload: State['debouncedFilters'] }
  | { type: 'SET_DID_REPORTS_FINISH_SETTING'; payload: State['didReportsFinishSetting'] };

export const OrderListView = (props: OrderListViewProps) => {
  const location = useLocation();
  return (
    <OrderListSearchParamsProvider key={location.pathname}>
      <View {...props} key={`${location.pathname}-list`} />
    </OrderListSearchParamsProvider>
  );
};

function View({ status = ORDER_STATUS_MAP.ALL, reportGuid }: OrderListViewProps) {
  const [searchParams] = useOrderListRouteSearchParams();
  const updateReportMutation = useUpdateReportMutation();
  const isReportView = Boolean(reportGuid);

  const orderListSearchParams = useOrderListSearchParamsContext();
  const [initialSearchParamState] = useState(() => ({
    sort: orderListSearchParams.getSort(),
    filters: orderListSearchParams.getFilters(),
  }));
  const orderListCompoundFilter = useOrderListCompoundFilter(initialSearchParamState.filters, {
    statusTab: status,
  });

  const [columnVisibility, setColumnVisibility] = useState(() => {
    if (isReportView) {
      return calculateColumnsVisibility(ORDER_COLUMNS);
    }

    const storedState = getStoredState(orderListCompoundFilter.state);
    return storedState ?? calculateColumnsVisibility(ORDER_COLUMNS);
  });
  const [{ globalSearch, didReportsFinishSetting, debouncedFilters }, dispatch] = useReducer(
    reducer,
    null,
    () => {
      return {
        globalSearch: searchParams.get(SEARCH_PARAM_KEYS.GLOBAL_SEARCH) ?? '',
        debouncedFilters: orderListCompoundFilter.state,
        didReportsFinishSetting: false,
      };
    },
  );
  const setDidReportsFinishSetting = useCallback((payload: boolean) => {
    dispatch({ type: 'SET_DID_REPORTS_FINISH_SETTING', payload });
  }, []);

  const handleGlobalSearchChange = useCallback((payload: string) => {
    dispatch({ type: 'SET_GLOBAL_SEARCH', payload });
  }, []);

  const handleDebouncedFiltersChange = useDebounce((newState: OrderListCompoundFilterState) => {
    storeConfigsIfAnyIsHiddenOnly(newState, columnVisibility);
    dispatch({ type: 'SET_DEBOUNCED_FILTERS', payload: newState });
  });

  const reportQuery = useReportQuery(reportGuid || '', {
    onSuccess: (data) => {
      const paramsState = fromOldUrlStateToNewFilterStructure(
        data.data.compoundFilterState as ExternalCompoundFilterState,
      );

      const newFilterState = buildInitialState(paramsState, undefined, {
        // keep open filters that are active whenever query is successful
        activeFilters: debouncedFilters
          .filter((filter) => filter.stateModifiers?.isActive)
          .map((filter) => filter.id),
      });

      orderListCompoundFilter.setState(newFilterState);
      handleDebouncedFiltersChange(newFilterState);
    },
  });

  const columns = useMemo(() => {
    return reportQuery.data?.data.columns
      ? getTableColumnsFromReportColumns(
          reportQuery.data?.data.columns as OrderReportColumnLiterals[],
        )
      : [];
  }, [reportQuery.data?.data.columns]);

  const visibilityState = useMemo(() => {
    if (!columns.length || !isReportView) return columnVisibility;

    return getReportColumnVisibility(columns, columnVisibility);
  }, [columnVisibility, columns, isReportView]);

  const tableProps = useReactTableStateProps({
    initialSortingState: initialSearchParamState.sort,
    initialPagination: { pageIndex: 1, pageSize: 25 },
  });
  const {
    state: { pagination, sorting },
  } = tableProps;

  const ordersRequestValues = useMemo(
    () =>
      mapOrdersRequestValues({
        filters: debouncedFilters,
        globalSearch,
        page: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sort: mapSortingState(sorting),
      }),
    [debouncedFilters, pagination, sorting, globalSearch],
  );

  const orderQuery = useOrdersQuery(
    ordersRequestValues,
    {
      enabled: !isReportView || Boolean(isReportView && reportQuery.data?.data),
      placeholderData: (previousData) => previousData,
    },
    reportGuid,
  );

  const orderRows = useMemo(
    () => fromOrderListResponseToJobTableRows(orderQuery.data?.results ?? []),
    [orderQuery.data],
  );

  const isCompoundFilterOpen = orderListCompoundFilter.state.some(
    (item) => item.stateModifiers?.isActive,
  );

  const visibleColumns = useMemo(
    () => ORDER_COLUMNS.filter((column) => visibilityState[column.id!]),
    [visibilityState],
  );

  const handleUpdateReportMutation = (props: HandleUpdateReportMutationProps = {}) => {
    const reportId = reportQuery.data?.data.id;
    if (!reportId) return;

    const currentlyVisibleColumns =
      props.visibleColumns ?? visibleColumns.map((column) => column.id!) ?? [];

    const compoundFilterState = props.compoundFilterState ?? orderListCompoundFilter.state;
    const filters = mapFilters(compoundFilterState);

    const name = props.reportValues?.reportName ?? reportQuery.data?.data.name ?? '';

    updateReportMutation.mutate(
      mapUpdateReportRequest({
        visibleColumns: currentlyVisibleColumns,
        compoundFilterState,
        filters,
        name,
        reportId,
      }),
    );
  };

  const handleCompoundFilterChange: CompoundFilterProviderProps['onChange'] = (
    state,
    action: CompoundFilterStateActions,
  ) => {
    orderListCompoundFilter.setState(state as OrderListCompoundFilterState);
    if (!didReportsFinishSetting) {
      setDidReportsFinishSetting(true);
    } else if (didReportsFinishSetting) {
      handleUpdateReportMutation({ compoundFilterState: state as OrderListCompoundFilterState });
    }

    if (action.type === 'TOGGLE_OPTION' || action.type === 'ADD_OPTION') {
      tableProps.onPaginationChange({ pageIndex: 1 });
    }
    orderListSearchParams.setFilters(state as OrderListCompoundFilterState);
    handleDebouncedFiltersChange(state as OrderListCompoundFilterState);
  };

  const handleSortingChange = (state: SortingState) => {
    tableProps.onSortingChange(state);
    orderListSearchParams.setSort(state);
  };

  return (
    <CompoundFilter.Provider
      state={orderListCompoundFilter.state}
      onChange={handleCompoundFilterChange}
    >
      <PageLayout
        header={
          <OrderListHeader globalSearch={globalSearch} onInputChange={handleGlobalSearchChange} />
        }
        topic={
          <OrderListTopic
            isReportView={isReportView}
            data={reportQuery.data?.data}
            orderListCompoundFilter={orderListCompoundFilter.state}
            ordersRequestValues={ordersRequestValues.body.filters}
            columnVisibility={visibilityState}
            onVisibilityChange={(newState) => {
              setColumnVisibility(newState);
              storeConfigsIfAnyIsHiddenOnly(orderListCompoundFilter.state, newState);
              handleUpdateReportMutation({
                visibleColumns: Object.keys(newState).filter((column) => newState[column]),
              });
            }}
            visibileColumns={visibleColumns.map((column) => column.id!)}
          />
        }
        subTopic={isCompoundFilterOpen && <SubTopicOptions />}
        topicTag={
          <div className="relative top-1 [&>div]:h-16">
            <Tag
              label={
                isReportView ? (
                  <ReportName
                    reportId={reportQuery.data?.data.id || ''}
                    reportName={reportQuery.data?.data.name || ''}
                  />
                ) : (
                  status.replace('_', ' ')
                )
              }
            />
          </div>
        }
        content={
          <OrderListContent
            data={orderRows}
            isLoading={orderQuery.isLoading || reportQuery.isLoading}
            isFetching={
              (!orderQuery.isLoading && orderQuery.isFetching) ||
              (!reportQuery.isLoading && reportQuery.isFetching)
            }
            columns={visibleColumns}
            isError={orderQuery.isError}
            tableProps={{ ...tableProps, onSortingChange: handleSortingChange }}
          >
            <OrderListPagination
              currentPage={tableProps.state.pagination.pageIndex}
              pageSize={tableProps.state.pagination.pageSize}
              isNextPageAvailable={Boolean(orderQuery.data?.next)}
              onPageSizeSelect={(pageSize) => tableProps.onPaginationChange({ pageSize })}
              onPageSelect={(pageIndex) => tableProps.onPaginationChange({ pageIndex })}
            />
          </OrderListContent>
        }
      />
    </CompoundFilter.Provider>
  );
}

function calculateColumnsVisibility<T extends { id?: string }[]>(columns: T): VisibilityState {
  return columns.reduce<VisibilityState>((acc, column) => {
    if (column.id === undefined) return acc;
    acc[column.id] = true;
    return acc;
  }, {});
}

function reducer(state: State, action: Action) {
  switch (action.type) {
    case 'SET_GLOBAL_SEARCH':
      return { ...state, globalSearch: action.payload };

    case 'SET_DEBOUNCED_FILTERS':
      return { ...state, debouncedFilters: action.payload };

    case 'SET_DID_REPORTS_FINISH_SETTING':
      return { ...state, didReportsFinishSetting: action.payload };

    default:
      return state;
  }
}

function mapSortingState(sorting: SortingState): GetOrdersSort {
  const { id, desc } = sorting?.[0] ?? { id: 'dateUpdated', desc: true };

  return {
    column: SORTABLE_FIELD_MAP[id as keyof typeof SORTABLE_FIELD_MAP],
    order: desc ? 'DESC' : 'ASC',
  };
}

/**
 * Validates and stores the current state for visible columns.
 * If all columns are visible, it will delete the stored state.
 * If any column is hidden, it will store the current state in local storage
 */
function storeConfigsIfAnyIsHiddenOnly(
  state: OrderListCompoundFilterState,
  columnVisibility: VisibilityState,
) {
  const noneHidden = Object.values(columnVisibility).every((value) => value);
  if (noneHidden) {
    ConfigStorage.deleteConfigs(getStorageKeyElements(state));
    return;
  }

  ConfigStorage.setConfigs(getStorageKeyElements(state), columnVisibility);
}

/**
 * Extract only the important values from filter state, which are the options selected.
 * This values will be used to build the key.
 * This is used to store the visibility state in local storage.
 */
function getStorageKeyElements(state: OrderListCompoundFilterState) {
  return state.flatMap((filter) => filter.options);
}

function getStoredState(state: OrderListCompoundFilterState) {
  const visibilityState = ConfigStorage.getConfigs<VisibilityState>(getStorageKeyElements(state));
  return visibilityState;
}
